import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCheckout, PaymentElement } from '@stripe/react-stripe-js/checkout';
import { IconLoader } from '@tabler/icons-react';

// Validate email with checkout
const validateEmail = async (email: string, checkout: any) => {
  const updateResult = await checkout.updateEmail(email);
  const isValid = updateResult.type !== "error";
  return { isValid, message: !isValid ? updateResult.error.message : null };
};

interface PaymentSectionProps {
  onComplete: () => void;
  email: string;
  setEmailError: (error: string | null) => void;
  setMessage: (message: string | null) => void;
  planPricing?: any;
  currentPlan?: any;
  sessionId?: string | null;
}

export const PaymentSection = ({
  onComplete,
  email,
  setEmailError,
  setMessage,
  planPricing,
  currentPlan,
  sessionId
}: PaymentSectionProps) => {
  // @ts-ignore - Type instantiation depth limitation in TypeScript
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stripePriceInfo, setStripePriceInfo] = useState<any>(null);

  const checkoutState = useCheckout();

  const trialPeriodDays = currentPlan?.pricing.trialPeriodDays;

  const isCheckoutReady = checkoutState.type !== 'loading' && checkoutState.type !== 'error';
  const isCheckoutLoading = checkoutState.type === 'loading';
  const isCheckoutError = checkoutState.type === 'error';

  // TODO: this is currently only in dev only
  // But it should probably be in PriceSummary as well so that the price is always correct
  //
  // Fetch actual price and product from Stripe session
  useEffect(() => {
    if (!sessionId || !isCheckoutReady) {
      return;
    }

    const fetchStripeSession = async () => {
      try {
        const API_URL = import.meta.env.VITE_WORKER_URL || '';
        const response = await fetch(`${API_URL}/api/get-receipt?session_id=${sessionId}`);
        if (response.ok) {
          const data = await response.json();
          setStripePriceInfo({
            product: data.product,
            payment: data.payment,
            lineItems: data.lineItems
          });
        }
      } catch (error) {
        console.error('Error fetching Stripe session:', error);
      }
    };

    fetchStripeSession();
  }, [sessionId, isCheckoutReady]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Don't submit if checkout is not ready
    if (checkoutState.type === 'loading' || checkoutState.type === 'error') {
      return;
    }

    // TypeScript guard: checkout is available when type is not 'loading' or 'error'
    if (!('checkout' in checkoutState)) {
      return;
    }

    const { checkout } = checkoutState;
    setIsSubmitting(true);
    setMessage(null);

    // Validate email if provided
    const { isValid, message: emailMsg } = await validateEmail(email, checkout);
    if (!isValid) {
      setEmailError(emailMsg);
      setMessage(emailMsg);
      setIsSubmitting(false);

      // scroll to email input
      const emailInput = document.getElementById('email-input-anchor');
      if (emailInput) {
        emailInput.scrollIntoView({ behavior: 'smooth' });
      }

      return;
    }

    const confirmResult = await checkout.confirm();

    if (confirmResult.type === 'error') {
      setMessage(confirmResult.error.message);
      setIsSubmitting(false);
    } else {
      // Success - onComplete will handle navigation
      onComplete();
    }
  };

  // Only show debug info in development
  const isDev = import.meta.env.DEV;

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="mb-4">
        <h4 className="text-sm font-medium text-slate-700 mb-3">
          {/* @ts-ignore - Type instantiation depth limitation */}
          {t('checkout.paymentMethod')}
        </h4>
        <div className="w-full min-h-[200px]">
          {isCheckoutError && (
            <div className="flex flex-col items-center justify-center py-12">
              <h3 className="text-base font-semibold text-slate-800 mb-2">
                {/* @ts-ignore - Type instantiation depth limitation */}
                {t('checkout.error')}
              </h3>
              <p className="text-sm text-slate-400 px-3 mb-4 text-center">
                {'error' in checkoutState ? checkoutState.error.message : 'Unknown error'}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600"
              >
                {/* @ts-ignore - Type instantiation depth limitation */}
                {t('checkout.reload')}
              </button>
            </div>
          )}
          {isCheckoutLoading && (
            <div className="w-full min-h-[200px] border border-slate-200 rounded-lg shadow-sm bg-white flex flex-col items-center justify-center">
              <IconLoader className="h-6 w-6 animate-spin text-slate-400 mb-2" />
              <p className="text-sm text-slate-400">
                {/* @ts-ignore - Type instantiation depth limitation */}
                {t('checkout.loadingDescription')}
              </p>
            </div>
          )}
          {isCheckoutReady && (
            <PaymentElement
              id="payment-element"
              options={{
                layout: {
                  type: 'accordion',
                  defaultCollapsed: false,
                  radios: true,
                  spacedAccordionItems: false
                }
              }}
            />
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting || !isCheckoutReady}
        className="w-full py-3 px-4 bg-sky-500 text-white rounded-lg hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
      >
        {isSubmitting ? (
          <>
            <IconLoader className="h-5 w-5 animate-spin" />
            <span>
              {/* @ts-ignore - Type instantiation depth limitation */}
              {t('checkout.processing')}
            </span>
          </>
        ) : isCheckoutReady ? (
          <span>
            {/* @ts-ignore - Type instantiation depth limitation */}
            {trialPeriodDays
              ? t('checkout.startTrial', { days: trialPeriodDays })
              : t('checkout.subscribe')}
          </span>
        ) : (
          <span>
            {/* @ts-ignore - Type instantiation depth limitation */}
            {t('checkout.loading')}
          </span>
        )}
      </button>

      <p className="text-xs text-slate-400 max-w-[80%] mx-auto text-center mt-2 mb-4 leading-normal">
        {/* @ts-ignore - Type instantiation depth limitation */}
        {t('checkout.description')}
      </p>

      {/* Debug: Show price information from Stripe */}
      {isDev && (stripePriceInfo || (planPricing && currentPlan)) && (
        <div className="w-screen mb-4 mt-8 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
          <div className="font-semibold mb-2">Debug - Price Info:</div>

          {/* Frontend Price (for comparison) */}
          {planPricing && currentPlan && (
            <div className="mb-3 pb-3 border-b border-slate-200">
              <div className="font-medium text-slate-700 mb-1">Frontend Price:</div>
              <div>Plan: {currentPlan?.name}</div>
              <div>Currency: {currentPlan?.pricing?.currency?.replace(/[^A-Z]/g, '') || 'USD'}</div>
              <div>Total Amount: {planPricing?.current || planPricing?.totalAmount || currentPlan?.pricing?.current || 0}</div>
              {planPricing?.referralSavings != null && planPricing.referralSavings > 0 && <div>Referral Savings: {planPricing.referralSavings}</div>}
            </div>
          )}

          {/* Stripe Price (actual) */}
          {stripePriceInfo ? (
            <div>
              <div className="font-medium text-slate-700 mb-1">Stripe Price (Actual):</div>
              {stripePriceInfo.product && (
                <>
                  <div>Product Name: {stripePriceInfo.product.name}</div>
                  {stripePriceInfo.product.description && (
                    <div>Description: {stripePriceInfo.product.description}</div>
                  )}
                </>
              )}
              {stripePriceInfo.payment && (
                <>
                  <div>Amount Total: {stripePriceInfo.payment.amount_total ? (stripePriceInfo.payment.amount_total / 100).toLocaleString() : 'N/A'}</div>
                  <div>Currency: {stripePriceInfo.payment.currency?.toUpperCase() || 'USD'}</div>
                </>
              )}
              {stripePriceInfo.lineItems && (
                <div className="mt-2">
                  <div className="font-medium">Line Items:</div>
                  <pre className="mt-1 text-xs overflow-auto max-h-32">
                    {JSON.stringify(stripePriceInfo.lineItems, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <div className="text-slate-400 italic">Loading Stripe price data...</div>
          )}
        </div>
      )}
    </form>
  );
};
